import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import FileUploadZone from './components/FileUploadZone';
import TemplateDownload from './components/TemplateDownload';
import UploadInstructions from './components/UploadInstructions';
import UploadProgress from './components/UploadProgress';
import ValidationSummary from './components/ValidationSummary';
import CSVPreview from './components/CSVPreview';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks';
import {
  findCandidatesByLinkedinUrls,
  getCandidatesStats,
  upsertCandidatesByLinkedinUrl,
} from '../../lib/api';
import { parseAndValidateCSV, readImportFile, toCandidateDbPayload } from '../../lib/utils/csvParser';

const ImportCandidates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refetch: refetchProfile } = useProfile();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [candidatesStats, setCandidatesStats] = useState({ total: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const hasLoadedStatsRef = useRef(false);
  const [preview, setPreview] = useState({
    rows: [],
    valid: [],
    errors: [],
    headerMapping: {},
    headerErrors: [],
    detectedHeaders: [],
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [existingUrls, setExistingUrls] = useState(new Set());
  const [consentAccepted, setConsentAccepted] = useState(false);
  const consentParagraphs = t('import.consentParagraphs', { returnObjects: true });
  const consentText = Array.isArray(consentParagraphs) ? consentParagraphs : [];

  const maxCandidates = profile?.max_candidates || 100;
  const currentCount = candidatesStats.total || 0;
  const remainingSlots = profile?.candidates_remaining != null
    ? Math.max(0, profile.candidates_remaining)
    : Math.max(0, maxCandidates - currentCount);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) {
        setStatsLoading(false);
        hasLoadedStatsRef.current = false;
        return;
      }
      const useSilent = hasLoadedStatsRef.current;
      try {
        if (!useSilent) setStatsLoading(true);
        const stats = await getCandidatesStats(user.id);
        setCandidatesStats(stats || { total: 0 });
        hasLoadedStatsRef.current = true;
      } catch (err) {
        console.error('Error fetching candidates stats:', err);
      } finally {
        if (!useSilent) setStatsLoading(false);
      }
    };
    fetchStats();
  }, [user?.id, validationResult?.successful]);

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setValidationResult(null);
    setUploadError(null);
    setPreview({
      rows: [],
      valid: [],
      errors: [],
      headerMapping: {},
      headerErrors: [],
      detectedHeaders: [],
    });
    setPreviewError(null);
    setExistingUrls(new Set());

    if (!file) return;

    setPreviewLoading(true);
    setDuplicateCheckLoading(false);
    try {
      const text = await readImportFile(file);
      const parsed = parseAndValidateCSV(text);
      setPreview(parsed);
      setPreviewLoading(false);

      if (parsed.rows.length === 0) {
        setPreviewError(t('import.noDataRows'));
        return;
      }

      if (user && parsed.valid.length > 0) {
        setDuplicateCheckLoading(true);
        try {
          const urls = parsed.valid.map((c) => c.linkedin_url);
          const existing = await findCandidatesByLinkedinUrls(user.id, urls);
          setExistingUrls(new Set(existing.keys()));
        } catch (err) {
          console.error('Duplicate check failed:', err);
        } finally {
          setDuplicateCheckLoading(false);
        }
      }
    } catch (err) {
      console.error('Preview parse error:', err);
      setPreviewError(err.message || t('import.failedToRead'));
      setPreviewLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user || !consentAccepted) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setValidationResult(null);

    try {
      setUploadProgress(20);
      // Reuse cached preview parse to avoid re-reading the file
      const { valid, invalid, errors } = preview.rows.length > 0
        ? preview
        : parseAndValidateCSV(await readImportFile(selectedFile));
      const invalidRowCount = invalid?.length || 0;

      if (valid.length === 0 && errors.length === 0) {
        setValidationResult({
          successful: 0,
          failed: 0,
          errors: [{ row: 0, field: 'File', message: t('import.noValidRows') }]
        });
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(40);

      // Re-check existing URLs at submit time (in case profile changed in another tab)
      const existing = await findCandidatesByLinkedinUrls(user.id, valid.map((c) => c.linkedin_url));
      const existingSet = new Set(existing.keys());

      const updatesAll = valid.filter((c) => existingSet.has(c.linkedin_url));
      const newAll = valid.filter((c) => !existingSet.has(c.linkedin_url));

      const maxCandidates = profile?.max_candidates || 100;
      const { total: currentCount } = await getCandidatesStats(user.id);
      const remainingSlots = Math.max(0, maxCandidates - currentCount);

      // Plan limit applies only to NEW candidates; updates don't grow the total.
      const newToImport = newAll.slice(0, remainingSlots);
      const skippedByLimit = newAll.length - newToImport.length;
      const toUpsert = [...newToImport, ...updatesAll];

      if (toUpsert.length === 0) {
        setValidationResult({
          successful: 0,
          failed: invalidRowCount + skippedByLimit,
          inserted: 0,
          updated: 0,
          errors: [
            ...errors,
            ...(remainingSlots === 0 && newAll.length > 0
              ? [{ row: 0, field: 'Limit', message: t('import.planLimitReached', { max: maxCandidates }) }]
              : []),
            ...(skippedByLimit > 0 && remainingSlots > 0
              ? [{ row: 0, field: 'Limit', message: t('import.skippedSlots', { skipped: skippedByLimit, remaining: remainingSlots }) }]
              : []),
          ],
        });
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(70);

      // Combine first_name + last_name → full_name (the DB column) and drop
      // the virtual first/last fields before sending the payload to Supabase.
      const toUpsertDB = toUpsert.map(toCandidateDbPayload);
      const { inserted, updated, skippedDuplicates = [] } = await upsertCandidatesByLinkedinUrl(user.id, toUpsertDB);
      const insertedCount = inserted?.length || 0;
      const updatedCount = updated?.length || 0;
      const skippedDuplicateCount = skippedDuplicates.length;

      setUploadProgress(100);

      setValidationResult({
        successful: insertedCount + updatedCount,
        failed: invalidRowCount + skippedByLimit + skippedDuplicateCount,
        inserted: insertedCount,
        updated: updatedCount,
        skippedDuplicates: skippedDuplicateCount,
        errors: [
          ...errors,
          ...(skippedByLimit > 0
            ? [{ row: 0, field: 'Limit', message: t('import.skippedPlanLimit', { skipped: skippedByLimit, max: maxCandidates }) }]
            : []),
          ...skippedDuplicates.map((dup) => ({
            row: 0,
            field: 'Duplicate',
            message: t('import.skippedDuplicate', { url: dup.linkedin_url }),
          })),
        ],
      });
      setCandidatesStats((prev) => ({ ...prev, total: prev.total + insertedCount }));
    if (insertedCount > 0) {
      await refetchProfile?.();
    }
    } catch (error) {
      console.error('Import error:', error);
      setUploadError(error.message || t('import.failedImport'));
      setValidationResult({
        successful: 0,
        failed: 1,
        inserted: 0,
        updated: 0,
        errors: [{ row: 0, field: 'Import', message: error.message || t('import.failedImport') }]
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setValidationResult(null);
    setUploadError(null);
    setPreview({
      rows: [],
      valid: [],
      errors: [],
      headerMapping: {},
      headerErrors: [],
      detectedHeaders: [],
    });
    setPreviewError(null);
    setExistingUrls(new Set());
    setConsentAccepted(false);
  };

  const pageLoading = (profileLoading && !profile) || statsLoading;

  if (pageLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('import.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{t('import.title')}</h1>
            <p className="text-muted-foreground">{t('import.subtitle')}</p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('import.slotsRemaining', {
                remaining: remainingSlots,
                current: currentCount,
                max: maxCandidates,
              })}
            </p>
          </div>
          <UploadInstructions />
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Template Download */}
          <TemplateDownload />

          {/* File Upload Zone */}
          <FileUploadZone 
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            disabled={uploading || remainingSlots === 0}
            maxCandidates={maxCandidates}
            currentCount={currentCount}
            remainingSlots={remainingSlots}
          />

          {/* CSV Preview */}
          {selectedFile && !validationResult && (
            <CSVPreview
              rows={preview.rows}
              remainingSlots={remainingSlots}
              existingUrls={existingUrls}
              parseError={previewError}
              loading={previewLoading}
              duplicateCheckLoading={duplicateCheckLoading}
              headerMapping={preview.headerMapping}
              headerErrors={preview.headerErrors}
              detectedHeaders={preview.detectedHeaders}
            />
          )}

          {/* Upload Progress */}
          {uploading && (
            <UploadProgress progress={uploadProgress} />
          )}

          {/* Validation Summary */}
          {validationResult && (
            <ValidationSummary result={validationResult} />
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive">
              {uploadError}
            </div>
          )}

          {!validationResult && (
            <Checkbox
              id="import-consent"
              className="items-start rounded-lg border border-border bg-card p-4"
              checked={consentAccepted}
              disabled={uploading}
              onChange={(event) => setConsentAccepted(Boolean(event?.target?.checked))}
              label={(
                <span className="block space-y-2 text-sm font-normal leading-relaxed text-foreground">
                  {consentText.map((paragraph) => (
                    <span key={paragraph} className="block">{paragraph}</span>
                  ))}
                </span>
              )}
            />
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/candidates-list')}
              variant="outline"
              iconName="ArrowLeft"
              size="lg"
            >
              {t('common.backToCandidates')}
            </Button>

            {(() => {
              const newCount = preview.valid.filter((c) => !existingUrls.has(c.linkedin_url)).length;
              const updateCount = preview.valid.length - newCount;
              const newToImport = Math.min(newCount, remainingSlots);
              const totalToWrite = newToImport + updateCount;
              const buttonDisabled =
                !consentAccepted ||
                !selectedFile ||
                uploading ||
                !user ||
                previewLoading ||
                !!previewError ||
                (preview.headerErrors && preview.headerErrors.length > 0) ||
                totalToWrite === 0;

              const buttonLabel = (() => {
                if (totalToWrite === 0) return t('import.uploadFile');
                if (newToImport > 0 && updateCount > 0) {
                  return t('import.addAndUpdate', { newCount: newToImport, updateCount });
                }
                if (updateCount > 0) {
                  return t('import.updateCandidates', { count: updateCount });
                }
                return t('import.addCandidates', { count: newToImport });
              })();

              return !validationResult && (
                <Button
                  onClick={handleUpload}
                  disabled={buttonDisabled}
                  loading={uploading}
                  iconName="Upload"
                  size="lg"
                >
                  {buttonLabel}
                </Button>
              );
            })()}

            {validationResult && (
              <Button
                onClick={handleReset}
                iconName="RefreshCw"
                size="lg"
              >
                {t('import.uploadAnother')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ImportCandidates;
